<template>
	<div class="newsletter-send">
		<ModalHeader
			:title="getString('send_newsletter')"
		/>

		<!-- Show cancel schedule UI if newsletter is scheduled -->
		<div v-if="isScheduled" class="scheduled-content">
			<div class="scheduled-info">
				<div class="scheduled-label">{{ getString('newsletter_scheduled_for_date', [formattedScheduledDate]) }}</div>
			</div>
			<AppButton
				:text="getString('cancel_schedule')"
				:outline="true"
				@click="handleCancelSchedule"
				:loading="cancelling"
				:disabled="cancelling"
				class="cancel-button"
			/>
		</div>

		<!-- Show section selection and send/schedule buttons for draft status -->
		<div v-else>
			<div class="sections-container">
				<div
					v-for="section in sections"
					:key="section.id"
					class="section-item"
					:class="{ active: selectedSections.includes(section.id) }"
					@click="toggleSection(section.id)"
				>
					<div class="section-title">{{ getString(section.titleKey) }}</div>
					<div class="section-description">{{ getString(section.descriptionKey) }}</div>
					<div v-if="shouldShowWarning(section.id)" class="privacy-warning">
						{{ getString('privacy_warning_orders_reservations') }}
					</div>
				</div>
			</div>

			<div class="buttons-container">
				<AppButton
					:text="getString('schedule_it')"
					:outline="true"
					@click="handleSchedule"
					:loading="loadingRecipients"
					:disabled="loadingRecipients || sending"
					class="action-button"
				/>
				<AppButton
					:text="getString('send_now')"
					:outline="true"
					@click="handleSend"
					:loading="loadingRecipients"
					:disabled="loadingRecipients || sending"
					class="action-button"
				/>
			</div>
		</div>

		<AppPopup
			ref="send-confirm"
			:title="getString('confirm_send_newsletter')"
			:body-text="confirmMessage"
			:buttons="[
				{text: getString('cancel'), negative: true, loading: sending},
				{text: getString('send'), callback: confirmSend, loading: sending}
			]"
		/>

		<NewsletterSchedule
			:active="showSchedulePicker"
			:recipients-count="recipientsCount"
			:selected-sections="selectedSections"
			@toggling-popup="togglingSchedulePicker"
			@close="handleScheduleClose"
		/>
		<RechargeBottomSheet
			:active="showRechargeBottomSheet"
			@close="handleRechargeClose"
		/>
	</div>
</template>

<script>
import { useLanguageStore, useNotificationStore } from '@/shared/stores';
import { useUserStore, useNewsletterStore } from '@/console-new/stores';
import ModalHeader from '@/console-new/components/shared/ModalHeader.vue';
import AppPopup from '@/shared/components/AppPopup.vue';
import AppButton from '@/shared/components/AppButton.vue';
import NewsletterSchedule from '@/console-new/components/marketing/NewsletterSchedule.vue';
import RechargeBottomSheet from '@/console-new/components/shared/RechargeBottomSheet.vue';
import WindowWidth from '@/console-new/mixins/WindowWidth';
import API from '@/shared/helpers/API';

export default {
	setup() {
		const languageStore = useLanguageStore();
		const notificationStore = useNotificationStore();
		const newsletterStore = useNewsletterStore();
		const userStore = useUserStore();
		return {
			languageStore,
			getString: languageStore.getString,
			showNotification: notificationStore.showNotification,
			userStore,
			newsletterStore
		};
	},
	name: "NewsletterSend",
	components: {
		ModalHeader,
		AppPopup,
		AppButton,
		NewsletterSchedule,
		RechargeBottomSheet
	},
	mixins: [WindowWidth],
	data() {
		return {
			sending: false,
			loadingRecipients: false,
			recipientsCount: 0,
			selectedSections: [],
			showSchedulePicker: false,
			cancelling: false,
			showRechargeBottomSheet: false,
			sections: [
				{
					id: 'online_orders',
					titleKey: 'contacts_from_online_orders',
					descriptionKey: 'contacts_from_online_orders_desc'
				},
				{
					id: 'reservations',
					titleKey: 'contacts_from_reservations',
					descriptionKey: 'contacts_from_reservations_desc'
				},
				{
					id: 'customers_registry',
					titleKey: 'contacts_from_customers_registry',
					descriptionKey: 'contacts_from_customers_registry_desc'
				}
			]
		}
	},
	computed: {
		currentNewsletter() {
			if (!this.newsletterStore.businessNewsletters || !this.newsletterStore.editingNewsletterId) {
				return null;
			}
			return this.newsletterStore.businessNewsletters.find(
				n => n.business_newsletter_id === this.newsletterStore.editingNewsletterId
			);
		},
		isScheduled() {
			return this.currentNewsletter?.status === 'scheduled';
		},
		formattedScheduledDate() {
			if (!this.currentNewsletter?.scheduled_at) {
				return '';
			}
			const date = new Date(this.currentNewsletter.scheduled_at);
			return date.toLocaleString(this.languageStore?.lang || 'it', {
				year: 'numeric',
				month: 'short',
				day: 'numeric',
				hour: '2-digit',
				minute: '2-digit'
			});
		},
		creditsRequired() {
			return Math.ceil(this.recipientsCount / 2);
		},
		confirmMessage() {
			return this.getString('send_to_x_recipients_y_credits', [this.recipientsCount.toLocaleString(), this.creditsRequired]);
		},
		bottomSheetWidth() {
			if (this.windowWidth < 550) {
				return "100%";
			} else if (this.windowWidth < 768) {
				return "550px";
			} else {
				return "720px";
			}
		}
	},
	methods: {
		shouldShowWarning(sectionId) {
			// Show warning only for online_orders and reservations if selected and force_privacy is not active
			const isOrdersOrReservations = sectionId === 'online_orders' || sectionId === 'reservations';
			const isSelected = this.selectedSections.includes(sectionId);
			return isOrdersOrReservations && isSelected && !this.userStore.currBusiness?.force_privacy;
		},
		toggleSection(sectionId) {
			const index = this.selectedSections.indexOf(sectionId);
			if (index > -1) {
				this.selectedSections.splice(index, 1);
			} else {
				this.selectedSections.push(sectionId);
			}
		},
		togglingSchedulePicker(v) {
			this.showSchedulePicker = v;
		},
		handleScheduleClose() {
			this.showSchedulePicker = false;
			this.$emit('close');
		},
		handleRechargeClose(success) {
			this.showRechargeBottomSheet = false;
			if (success) {
				this.userStore.refreshBusiness(this.userStore.currBusinessId);
			}
		},
		handleSchedule() {
			if (this.selectedSections.length === 0) {
				this.showNotification('select-at-least-one-section');
				return;
			}

			if (this.loadingRecipients || this.sending) {
				return;
			}

			this.loadingRecipients = true;

			// Get recipients count first
			API.init().getNewsletterRecipientsCount(
				this.userStore.currBusinessId,
				this.newsletterStore.editingNewsletterId,
				this.selectedSections,
				(response) => {
					this.loadingRecipients = false;

					if (response.result === 'OK') {
						this.recipientsCount = response.count || 0;

						if (this.recipientsCount === 0) {
							this.showNotification('no-recipients-found');
							return;
						}

						// Show schedule picker
						this.showSchedulePicker = true;
					} else {
						this.showNotification('generic-error');
					}
				}
			);
		},
		handleSend() {
			if (this.selectedSections.length === 0) {
				this.showNotification('select-at-least-one-section');
				return;
			}

			if (this.loadingRecipients || this.sending) {
				return;
			}

			this.loadingRecipients = true;

			// Get recipients count first
			API.init().getNewsletterRecipientsCount(
				this.userStore.currBusinessId,
				this.newsletterStore.editingNewsletterId,
				this.selectedSections,
				(response) => {
					this.loadingRecipients = false;

					if (response.result === 'OK') {
						this.recipientsCount = response.count || 0;

						if (this.recipientsCount === 0) {
							this.showNotification('no-recipients-found');
							return;
						}

						// Show confirmation popup
						this.$refs['send-confirm'].toggleStatus(true);
					} else {
						this.showNotification('generic-error');
					}
				}
			);
		},
		confirmSend() {
			if (this.sending) {
				return;
			}

			this.sending = true;

			API.init().sendNewsletter(
				this.userStore.currBusinessId,
				this.newsletterStore.editingNewsletterId,
				this.selectedSections,
				null,
				(response) => {
					this.sending = false;

					if (response.result === 'OK') {
						this.showNotification('newsletter-send-started');
						this.newsletterStore.editingNewsletterId = null;
						// Refresh newsletter list to show updated status
						this.newsletterStore.businessNewsletters = null;
						this.newsletterStore.getBusinessNewsletters();
						this.$emit('close');
					} else {
						if (response.details === 'content_rejected') {
							const reason = response.reason || 'Content did not pass moderation';
							this.showNotification('newsletter-content-rejected', [reason]);
						} else if (response.details === 'newsletter_pending') {
							this.showNotification('newsletter-pending');
						} else if (response.details === 'no_recipients') {
							this.showNotification('no-recipients-found');
						} else if (response.details === 'business_link_not_configured') {
							this.showNotification('business-link-not-configured');
						} else if (response.details === 'insufficient_credits') {
							this.showRechargeBottomSheet = true;
							const requiredCredits = response.required_credits || this.creditsRequired;
							this.showNotification('insufficient-credits-for-newsletter', [requiredCredits]);
						} else {
							this.showNotification('generic-error');
						}
					}
				}
			);
		},
		handleCancelSchedule() {
			if (this.cancelling) {
				return;
			}

			this.cancelling = true;

			API.init().cancelScheduledNewsletter(
				this.userStore.currBusinessId,
				this.newsletterStore.editingNewsletterId,
				(response) => {
					this.cancelling = false;

					if (response.result === 'OK') {
						this.showNotification('newsletter-schedule-cancelled');
						// Refresh newsletter to get updated status (now draft)
						this.newsletterStore.getNewsletter(this.newsletterStore.editingNewsletterId);
						// Refresh newsletter list
						this.newsletterStore.businessNewsletters = null;
						this.newsletterStore.getBusinessNewsletters();
					} else {
						if (response.details === 'newsletter_not_scheduled') {
							this.showNotification('newsletter-not-scheduled');
						} else {
							this.showNotification('generic-error');
						}
					}
				}
			);
		}
	}
}
</script>

<style scoped lang="scss">
.newsletter-send {
	.scheduled-content {
		display: flex;
		flex-direction: column;
		gap: 20px;

		.scheduled-info {
			padding: 16px;
			border-radius: 13px;
			background-color: var(--color-bg-primary-2);
			border: 1px solid var(--color-primary-alpha-4);

			.scheduled-label {
				font-size: 15px;
				font-weight: 500;
				color: var(--color-txt);
				text-align: center;
			}
		}

		.cancel-button {
			width: 100%;
			height: 42px;
			max-width: unset;
		}
	}

	.sections-container {
		display: flex;
		flex-direction: column;
		gap: 12px;
		margin-bottom: 20px;
	}

	.buttons-container {
		display: flex;
		gap: 12px;
		width: 100%;

		.action-button {
			flex: 1;
			height: 42px;
		}
	}

	.section-item {
		padding: 12px;
		border: 1px solid transparent;
		border-radius: 13px;
		cursor: pointer;
		transition: all 0.3s ease;
		background-color: var(--color-bg-primary-2);

		&:hover {
			border: 1px solid var(--color-primary-alpha-4);
		}

		&.active {
			border: 1px solid var(--color-primary);
			background-color: var(--color-bg-primary-2);
		}

		.section-title {
			font-size: 16px;
			font-weight: 600;
			color: var(--color-txt);
			margin-bottom: 6px;
		}

		.section-description {
			font-size: 13px;
			font-weight: 400;
			color: var(--color-txt-1);
			line-height: 1.4;
		}

		.privacy-warning {
			margin-top: 10px;
			padding: 10px;
			border-radius: 8px;
			background-color: var(--color-danger-alpha-1);
			border: 1px solid var(--color-danger);
			color: var(--color-danger);
			font-size: 12px;
			font-weight: 500;
			line-height: 1.4;
		}
	}
}
</style>
